import { FC, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useSnapshot } from 'valtio'

import { Modal, Checkbox, Button } from '@app/ui'
import { ModalType, ButtonType, ButtonSize } from '@app/types'
import { IconColor } from '@app/ui/Icon'
import { useGetTenantBookiesQuery, useSetFavoriteBookieMutation } from '@app/graphql'
import { CurrentCountryState, CurrentUserState } from '@app/storage'
import { UserCacheHelper } from '@app/helpers/userCache'

interface FavoriteBookieModalProps {
  open: boolean
  onClose: () => void
  onSaved?: () => void
}

const FavoriteBookieModal: FC<FavoriteBookieModalProps> = ({ open, onClose, onSaved }) => {
  const { t } = useTranslation()
  const { countryId } = useSnapshot(CurrentUserState)
  const { id: currentCountryId } = useSnapshot(CurrentCountryState)
  const resolvedCountryId = (currentCountryId as string) || (countryId as string) || null
  const { data, loading } = useGetTenantBookiesQuery({
    skip: !open,
    fetchPolicy: 'network-only',
    variables: { countryIds: resolvedCountryId ? [ resolvedCountryId ] : undefined },
  })
  const [ setFavoriteBookie, { loading: saving } ] = useSetFavoriteBookieMutation()

  const bookies = (data?.tenantBookies ?? []).filter((b) => b.isVisible && b.id != null)

  const [ selectedTenantBookieId, setSelectedTenantBookieId ] = useState<string | null>(null)

  const handleConfirm = async () => {
    if (selectedTenantBookieId == null) return
    const result = await setFavoriteBookie({
      variables: { tenantBookieId: selectedTenantBookieId },
      update: (cache, { data: saved }) => {
        const userId = CurrentUserState.id
        if (!userId) return
        const savedTenantBookieId = saved?.setFavoriteBookie?.tenantBookieId ?? selectedTenantBookieId
        cache.modify({
          id: cache.identify({ __typename: 'UserModel', id: userId }),
          fields: { favoriteTenantBookieId: () => savedTenantBookieId },
        })
      },
    })
    const resolvedTenantBookieId = result.data?.setFavoriteBookie?.tenantBookieId ?? selectedTenantBookieId
    CurrentUserState.favoriteTenantBookieId = resolvedTenantBookieId
    UserCacheHelper.updateFavoriteTenantBookieId(resolvedTenantBookieId)
    onClose()
    onSaved?.()
  }

  return (
    <StyledModal open={open} onClose={onClose} type={ModalType.bottomSheet} zIndex={10001}>
      <Sheet>
        <SheetHandle />
        <SheetTitle>{t('tip.chooseFavoriteBookie', 'Which bookmaker did you deposit on?')}</SheetTitle>

        <BookiesList>
          {!loading && bookies.map((bookie) => (
            <BookieRow key={bookie.id} onClick={() => setSelectedTenantBookieId(bookie.id)}>
              <StyledCheckbox
                checked={selectedTenantBookieId === bookie.id}
                onClick={() => setSelectedTenantBookieId(bookie.id)}
                selectedColor={IconColor.success}
                size={18}
              />
              <BookieLogoWrapper>
                {bookie.imageUrl ? (
                  <BookieLogo src={bookie.imageUrl} alt={bookie.name} />
                ) : (
                  <BookieNameFallback>{bookie.name}</BookieNameFallback>
                )}
              </BookieLogoWrapper>
            </BookieRow>
          ))}
        </BookiesList>

        <ConfirmButton
          type={ButtonType.buttonColor}
          size={ButtonSize.normal}
          fluid
          disabled={selectedTenantBookieId == null || saving}
          onClick={handleConfirm}
        >
          {t('common.confirm', 'Confirm')}
        </ConfirmButton>
      </Sheet>
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  && {
    z-index: 10001 !important;

    &.type-bottom-sheet {
      align-items: stretch !important;
      justify-content: flex-end !important;
    }

    .type-bottom-sheet {
      position: fixed !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      margin: 0 auto !important;
      max-width: 600px !important;
      background: var(--modal-background);
      border-radius: 16px 16px 0 0;
      padding: 0 !important;
      overflow: hidden;
      z-index: 10002;
    }
  }
`

const Sheet = styled.div`
  width: 100%;
  padding: 20px 16px calc(24px + env(safe-area-inset-bottom, 0px));
  background-color: var(--modal-background);
  border-radius: 16px 16px 0 0;
`

const SheetHandle = styled.div`
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background-color: var(--zinc-700);
  margin: 0 auto 16px;
`

const SheetTitle = styled.h3`
  margin: 0 0 16px;
  color: var(--text-primary);
  font-size: var(--font-size-large);
  font-weight: 700;
`

const BookiesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 50vh;
  overflow-y: auto;
  margin-bottom: 16px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--scrollbar-color);
    border-radius: 2px;
  }
  scrollbar-width: thin;
  scrollbar-color: var(--scrollbar-color) transparent;
`

const BookieRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background-color: var(--zinc-800);
  border-radius: 12px;
  cursor: pointer;

  [data-theme="light"] & {
    background-color: var(--bg-tertiary);
  }
`

const StyledCheckbox = styled(Checkbox)`
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
  }
`

const BookieLogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 80px;
  height: 28px;
`

const BookieLogo = styled.img`
  max-height: 34px;
  max-width: 120px;
  object-fit: contain;
`

const BookieNameFallback = styled.span`
  color: var(--text-primary);
  font-size: var(--font-size-normal);
  font-weight: 700;
  text-transform: uppercase;
`

const ConfirmButton = styled(Button)`
  width: 100%;
  height: 56px;
  font-size: var(--font-size-large);
`

export default FavoriteBookieModal
